<template>
  <div>
    <div v-if="!isHidden">
      <div
        v-if="size !== 'thumbnail'"
        class="overflow-x-auto overflow-y-hidden whitespace-nowrap mr-4 md:flex"
      >
        <button
          v-for="tab in tabs"
          :key="tab.data"
          class="px-1 -mb-px font-semibold hover:opacity-80"
          :class="[
            { 'opacity-50': activeTab.data !== tab.data },
            { 'text-blue-900 rounded-6': activeTab.data === tab.data },
          ]"
          @click="selectTab(tab)"
        >
          {{ tab.title }}
        </button>
      </div>
      <MChart :class="chartClass ?? ''" :chart-data="chartData" :options="options" :size="size">
        <template v-if="size !== 'thumbnail'" #caption>
          {{ $t('source') }} <XeLink /> | {{ $t('last_updated', { ago: '' }) }}
          <NuxtTime :datetime="timestamp" :locale="$lang" relative />
        </template>
        <template v-if="size === 'thumbnail'" #placeholder>
          <div class="w-full h-full rounded-4" />
        </template>
      </MChart>
    </div>

    <button v-else class="link text-gray-700 m-auto mt-3 block" @click="$emit('toggle-chart')">
      {{ $t('currency_chart.see_the_chart') }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import type { ChartData, ChartOptions } from 'chart.js'
import type {
  GQLCurrencyChartRates,
  GQLCurrencyChartRatesVariables,
} from './generated/currency-chart-rates'
import { chartOptions } from './options'
import QUERY from './currency-chart-rates.graphql'
import MChart from './m-chart.vue'

const emit = defineEmits(['toggle-chart', 'chart-data'])

type Tab = {
  data: string
  title: string
}

const props = defineProps<{
  currencyFrom: string
  currencyTo: string
  midMarketRate?: number | null
  hasDropdown?: boolean
  isHidden?: boolean
  size?: 'big' | 'small' | 'thumbnail'
  chartClass?: string | object
  chartOptionOverrides?: ChartOptions<'line'>
  noFill?: boolean
}>()

const { $apollo, $t, $date } = useNuxtApp()
const logger = useLogger('currency-chart')

const isLoading = ref(false)
const historyData = ref<Record<string, ChartData> | null>(null)

function dateMinuteAgo() {
  return Date.now() - (60_000 + 1)
}
const timestamp = ref(dateMinuteAgo())

const currencies = { currencyFrom: props.currencyFrom, currencyTo: props.currencyTo }
const { data, error, status } = await useAsyncCachedData<GQLCurrencyChartRates>(
  `currencyChart-${currencies.currencyFrom}-${currencies.currencyTo}`,
  async () => {
    historyData.value = null
    const { data, errors } = await $apollo.query<
      GQLCurrencyChartRates,
      GQLCurrencyChartRatesVariables
    >({
      query: QUERY,
      variables: currencies,
    })
    if (errors) {
      throw createGraphQLError(errors, 'Failed to fetch exchange rates')
    }
    timestamp.value = dateMinuteAgo()
    return data
  },
  {
    server: false,
    ttl: 60, // the exchange rate changes every minute
  },
)
watch(
  error,
  () => {
    if (error.value) {
      logger.error(error.value)
    }
  },
  { immediate: true },
)
watch(
  status,
  () => {
    isLoading.value = status.value === 'pending'
  },
  { immediate: true },
)
watch(
  data,
  () => {
    if (data.value) {
      const ratesWeek = [...data.value.ratesWeek]
      const rates2weeks = [...data.value.rates2weeks]
      const rates2months = [...data.value.rates2months]

      if (props.midMarketRate) {
        const point = { timestamp: Date.now(), rate: props.midMarketRate }
        ratesWeek.push(point)
        rates2weeks.push(point)
        rates2months[rates2months.length - 1] = point
      }

      historyData.value = {
        '7': toChartData(ratesWeek),
        '14': toChartData(rates2weeks),
        '60': toChartData(rates2months),
      }
      emit('chart-data', historyData.value)
    }
  },
  { immediate: true },
)

function toChartData(rates: GQLCurrencyChartRates['ratesWeek']): ChartData {
  const labels = rates.map((point) => $date(point.timestamp).format('L'))
  // Exchange rate is not localised here because Number() only accepts English strings
  const data = rates.map((point) => Number(formatRate(point.rate)))
  return { labels, datasets: [{ data, fill: !props.noFill }] }
}

const chartData = computed(() => {
  if (historyData.value) {
    return historyData.value[activeTab.value.data]
  }
  return null
})

const options = computed<ChartOptions>(() => {
  return props.chartOptionOverrides
    ? { ...chartOptions, ...props.chartOptionOverrides }
    : chartOptions
})

const tabs = computed<Tab[]>(() => {
  return [
    {
      title: $t('currency_chart.last_days', { count: 60 }),
      data: '60',
    },
    {
      title: $t('currency_chart.last_days', { count: 14 }),
      data: '14',
    },
    {
      title: $t('currency_chart.last_days', { count: 7 }),
      data: '7',
    },
  ]
})
const activeTab = ref(tabs.value[0])
function selectTab(tab: Tab) {
  activeTab.value = tab
}
</script>
